// SPDX-License-Identifier: AGPL-3.0-or-later
import { useId, useMemo, useState, type FormEvent } from "react";
import { splitAmount, splitProblem, type Cents, type IsoDate, type SplitRule } from "@almanac/domain";
import { useApp } from "../lib/app.js";
import { groupView, iOwe, owedItems, owedToMe, repaymentOffers, type GroupView } from "../lib/groups.js";
import { useI18n } from "../lib/i18n.js";
import { ME, uuid, type Group, type GroupMember, type Repeat, type Transaction } from "../lib/model.js";
import { parseMoney, parsePercent } from "../lib/parse.js";
import { useStore } from "../lib/store.js";

type SplitKind = SplitRule["kind"];
const KINDS: SplitKind[] = ["equal", "shares", "percent", "exact"];
const REPEATS: Repeat[] = ["none", "weekly", "biweekly", "monthly", "yearly"];
/** How far back outgoing transactions are offered when linking an expense. */
const LINK_DAYS = 60;

export function Groups() {
  const { store, today, announce } = useApp();
  const { groups, ledger } = useStore(store);
  const { t, money, date } = useI18n();
  const views = useMemo(() => groups.map((g) => groupView(g, ledger, today)), [groups, ledger, today]);
  const offers = useMemo(() => repaymentOffers(views, ledger, today), [views, ledger, today]);
  const [selected, setSelected] = useState<string | null>(null);
  const view = views.find((v) => v.group.id === selected) ?? views[0];

  return (
    <>
      <p className="hint" style={{ marginTop: 0 }}>
        {t("groups.intro")}
      </p>

      {offers.length > 0 && (
        <section className="card" aria-labelledby="offers-title" style={{ marginBottom: 16 }}>
          <h2 id="offers-title">{t("groups.offers")}</h2>
          <ul className="offers">
            {offers.map((o) => {
              const v = views.find((x) => x.group.id === o.groupId)!;
              const name = memberName(v.group, o.memberId, t("groups.you"));
              const tx = o.transaction;
              return (
                <li key={tx.id}>
                  <p>
                    {t("groups.offer", { name, amount: money(tx.amount), date: date(tx.date, "short"), group: v.group.name })}
                    <span className="hint"> ({tx.description})</span>
                  </p>
                  <div className="row">
                    <button
                      type="button"
                      className="primary"
                      onClick={() => {
                        store.recordPayment(o.groupId, { date: tx.date, from: o.memberId, to: ME, amount: tx.amount, txId: tx.id });
                        announce(t("groups.paidRecorded", { name, amount: money(tx.amount) }));
                      }}
                    >
                      {t("groups.offerYes")}
                    </button>
                    <button type="button" onClick={() => store.declineOffer(o.groupId, tx.id)}>
                      {t("groups.offerNo")}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <div className="groups-layout">
        <section className="card" aria-labelledby="groups-title">
          <h2 id="groups-title">{t("groups.list")}</h2>
          {views.length === 0 ? (
            <p className="hint">{t("groups.none")}</p>
          ) : (
            <ul className="group-list">
              {views.map((v) => {
                const owed = sum(owedToMe(v));
                const owe = sum(iOwe(v));
                return (
                  <li key={v.group.id}>
                    <button type="button" aria-current={v === view ? "true" : undefined} onClick={() => setSelected(v.group.id)}>
                      <strong>{v.group.name}</strong>
                      <span className="hint">
                        {owed > 0 ? t("groups.owedShort", { amount: money(owed) }) : owe > 0 ? t("groups.oweShort", { amount: money(owe) }) : t("groups.settled")}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <NewGroup
            onCreate={(g) => {
              store.saveGroup(g);
              setSelected(g.id);
              announce(t("groups.created", { name: g.name }));
            }}
          />
        </section>
        {view && <GroupDetail key={view.group.id} view={view} ledger={ledger} today={today} />}
      </div>
    </>
  );
}

const sum = (m: ReadonlyMap<string, Cents>) => [...m.values()].reduce((s, v) => s + v, 0);

function memberName(group: Group, id: string, you: string): string {
  return id === ME ? you : (group.members.find((m) => m.id === id)?.name ?? "?");
}

/** Names typed as "Sam, Jo, Kim" become members alongside you. */
function NewGroup({ onCreate }: { onCreate: (group: Group) => void }) {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [people, setPeople] = useState("");
  const id = useId();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const names = [...new Set(people.split(",").map((p) => p.trim()).filter(Boolean))];
    if (!name.trim()) return;
    onCreate({
      id: uuid(),
      name: name.trim().slice(0, 80),
      members: [{ id: ME, name: "" }, ...names.map((n) => ({ id: uuid(), name: n.slice(0, 60) }))],
      expenses: [],
      recurring: [],
      payments: [],
      declined: [],
    });
    setName("");
    setPeople("");
  };
  return (
    <form onSubmit={submit} className="new-group" aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`}>{t("groups.new")}</h3>
      <div className="field">
        <label htmlFor={`${id}-name`}>{t("groups.name")}</label>
        <input type="text" id={`${id}-name`} value={name} maxLength={80} placeholder={t("groups.namePlaceholder")} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor={`${id}-people`}>{t("groups.people")}</label>
        <input type="text" id={`${id}-people`} value={people} aria-describedby={`${id}-help`} onChange={(e) => setPeople(e.target.value)} />
        <p className="hint" id={`${id}-help`}>
          {t("groups.peopleHelp")}
        </p>
      </div>
      <button type="submit" disabled={!name.trim()}>
        {t("groups.create")}
      </button>
    </form>
  );
}

function GroupDetail({ view, ledger, today }: { view: GroupView; ledger: readonly Transaction[]; today: IsoDate }) {
  const { store, announce } = useApp();
  const { t, money, date } = useI18n();
  const group = view.group;
  const you = t("groups.you");
  const nameOf = (id: string) => memberName(group, id, you);
  const owed = owedToMe(view);
  const owe = iOwe(view);
  const [newMember, setNewMember] = useState("");
  const [allCharges, setAllCharges] = useState(false);
  const SHOWN = 8;
  const active = new Set([
    ...view.charges.flatMap((c) => [c.paidBy, ...c.shares.keys()]),
    ...group.payments.flatMap((p) => [p.from, p.to]),
  ]);
  const save = (next: Group) => store.saveGroup(next);

  return (
    <section className="card group-detail" aria-labelledby="group-title">
      <h2 id="group-title">{group.name}</h2>

      <h3>{t("groups.balances")}</h3>
      <ul className="balances">
        {group.members
          .filter((m) => m.id !== ME)
          .map((m) => {
            const theyOwe = owed.get(m.id) ?? 0;
            const iOweThem = owe.get(m.id) ?? 0;
            return (
              <li key={m.id}>
                <span>
                  {theyOwe > 0
                    ? t("groups.owesYou", { name: m.name, amount: money(theyOwe) })
                    : iOweThem > 0
                      ? t("groups.youOwe", { name: m.name, amount: money(iOweThem) })
                      : t("groups.isSettled", { name: m.name })}
                </span>
                {(theyOwe > 0 || iOweThem > 0) && (
                  <button
                    type="button"
                    onClick={() => {
                      const amount = theyOwe || iOweThem;
                      store.recordPayment(group.id, { date: today, from: theyOwe ? m.id : ME, to: theyOwe ? ME : m.id, amount, txId: null });
                      announce(theyOwe ? t("groups.paidRecorded", { name: m.name, amount: money(amount) }) : t("groups.youPaidRecorded", { name: m.name, amount: money(amount) }));
                    }}
                  >
                    {t("groups.markPaid")}
                  </button>
                )}
                {theyOwe > 0 && <Reminder view={view} member={m} owed={theyOwe} />}
              </li>
            );
          })}
      </ul>
      {view.transfers.some((tr) => tr.from !== ME && tr.to !== ME) && (
        <>
          <h3>{t("groups.between")}</h3>
          <ul>
            {view.transfers
              .filter((tr) => tr.from !== ME && tr.to !== ME)
              .map((tr) => (
                <li key={`${tr.from}|${tr.to}`}>{t("groups.pays", { from: nameOf(tr.from), to: nameOf(tr.to), amount: money(tr.amount) })}</li>
              ))}
          </ul>
        </>
      )}

      <ExpenseForm group={group} ledger={ledger} today={today} />

      <h3>{t("groups.expenses")}</h3>
      {view.charges.length === 0 ? (
        <p className="hint">{t("groups.noExpenses")}</p>
      ) : (
        <table className="charges">
          <thead>
            <tr>
              <th scope="col">{t("groups.date")}</th>
              <th scope="col">{t("groups.what")}</th>
              <th scope="col">{t("groups.paidBy")}</th>
              <th scope="col" className="num">
                {t("groups.amount")}
              </th>
              <th scope="col" className="num">
                {t("groups.yourShare")}
              </th>
              <th scope="col">
                <span className="visually-hidden">{t("groups.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {(allCharges ? view.charges : view.charges.slice(0, SHOWN)).map((c) => (
              <tr key={c.id}>
                <td>{date(c.date, "short")}</td>
                <td>
                  {c.description}
                  {c.recurringId && <span className="hint"> · {t("groups.repeats")}</span>}
                  {c.txId && c.paidBy === ME && <span className="hint"> · {t("groups.linked")}</span>}
                </td>
                <td>{nameOf(c.paidBy)}</td>
                <td className="num">{money(c.amount)}</td>
                <td className="num">{money(c.shares.get(ME) ?? 0)}</td>
                <td>
                  {!c.recurringId && (
                    <button
                      type="button"
                      className="link-button"
                      aria-label={t("groups.deleteExpense", { name: c.description })}
                      onClick={() => {
                        if (!confirm(t("groups.confirmDeleteExpense", { name: c.description }))) return;
                        save({ ...group, expenses: group.expenses.filter((e) => e.id !== c.id) });
                      }}
                    >
                      {t("groups.delete")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {view.charges.length > SHOWN && (
        <button type="button" className="link-button" aria-expanded={allCharges} onClick={() => setAllCharges(!allCharges)}>
          {allCharges ? t("groups.showFewer") : t("groups.showAll", { count: view.charges.length })}
        </button>
      )}

      {group.recurring.length > 0 && (
        <>
          <h3>{t("groups.recurring")}</h3>
          <ul className="recurring">
            {group.recurring.map((r) => {
              const ended = r.until !== null && r.until < today;
              return (
                <li key={r.id}>
                  <span>
                    {t("groups.recurringLine", { name: r.description, amount: money(r.amount), repeat: t(`events.repeat.${r.repeat}`), from: date(r.start, "short") })}
                    {ended && ` · ${t("groups.ended", { date: date(r.until!, "short") })}`}
                  </span>
                  {!ended && (
                    <button
                      type="button"
                      onClick={() => {
                        save({ ...group, recurring: group.recurring.map((x) => (x.id === r.id ? { ...x, until: today } : x)) });
                        announce(t("groups.stopped", { name: r.description }));
                      }}
                    >
                      {t("groups.stop")}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      {group.payments.length > 0 && (
        <>
          <h3>{t("groups.payments")}</h3>
          <ul>
            {[...group.payments]
              .sort((a, b) => (a.date < b.date ? 1 : -1))
              .slice(0, 12)
              .map((p) => (
                <li key={p.id}>
                  {p.to === ME
                    ? t("groups.paidYou", { from: nameOf(p.from), amount: money(p.amount), date: date(p.date, "short") })
                    : p.from === ME
                      ? t("groups.youPaid", { to: nameOf(p.to), amount: money(p.amount), date: date(p.date, "short") })
                      : t("groups.paymentLine", { from: nameOf(p.from), to: nameOf(p.to), amount: money(p.amount), date: date(p.date, "short") })}{" "}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => save({ ...group, payments: group.payments.filter((x) => x.id !== p.id) })}
                    aria-label={t("groups.undoPaymentLabel", { from: nameOf(p.from), amount: money(p.amount) })}
                  >
                    {t("groups.undo")}
                  </button>
                </li>
              ))}
          </ul>
        </>
      )}

      <h3>{t("groups.members")}</h3>
      <ul className="members">
        {group.members.map((m) => (
          <li key={m.id}>
            {nameOf(m.id)}
            {m.id !== ME && !active.has(m.id) && (
              <button
                type="button"
                className="link-button"
                onClick={() => save({ ...group, members: group.members.filter((x) => x.id !== m.id) })}
                aria-label={t("groups.removeMember", { name: m.name })}
              >
                {t("groups.remove")}
              </button>
            )}
          </li>
        ))}
      </ul>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          const n = newMember.trim();
          if (!n) return;
          save({ ...group, members: [...group.members, { id: uuid(), name: n.slice(0, 60) }] });
          setNewMember("");
        }}
      >
        <label className="visually-hidden" htmlFor="new-member">
          {t("groups.addMember")}
        </label>
        <input type="text" id="new-member" value={newMember} placeholder={t("groups.addMember")} onChange={(e) => setNewMember(e.target.value)} />
        <button type="submit" disabled={!newMember.trim()}>
          {t("groups.add")}
        </button>
      </form>

      <p style={{ marginTop: 24 }}>
        <button
          type="button"
          className="danger"
          onClick={() => {
            if (!confirm(t("groups.confirmDelete", { name: group.name }))) return;
            store.deleteGroup(group.id);
            announce(t("groups.deleted", { name: group.name }));
          }}
        >
          {t("groups.deleteGroup")}
        </button>
      </p>
    </section>
  );
}

/** A message listing what someone owes, to copy into a text or email. The app never sends it. */
function Reminder({ view, member, owed }: { view: GroupView; member: GroupMember; owed: Cents }) {
  const { t, money, date } = useI18n();
  const [copied, setCopied] = useState(false);
  const id = useId();
  const items = owedItems(view, member.id, owed);
  const text = [
    t("groups.reminderHello", { name: member.name.split(/\s+/)[0]!, group: view.group.name, amount: money(owed) }),
    ...items.map((i) => `- ${i.charge.description}, ${date(i.charge.date, "short")}: ${money(i.share)}`),
    t("groups.reminderThanks"),
  ].join("\n");
  return (
    <details className="reminder">
      <summary>{t("groups.reminder", { name: member.name })}</summary>
      <label htmlFor={id} className="visually-hidden">
        {t("groups.reminderLabel", { name: member.name })}
      </label>
      <textarea id={id} readOnly rows={items.length + 3} value={text} />
      <div className="row">
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(text).then(() => setCopied(true));
          }}
        >
          {t("groups.copy")}
        </button>
        <span role="status">{copied ? t("groups.copied") : ""}</span>
      </div>
      <p className="hint">{t("groups.reminderHint")}</p>
    </details>
  );
}

function ExpenseForm({ group, ledger, today }: { group: Group; ledger: readonly Transaction[]; today: IsoDate }) {
  const { store, announce } = useApp();
  const { t, money, date } = useI18n();
  const id = useId();
  const you = t("groups.you");
  const members = group.members;
  const [description, setDescription] = useState("");
  const [amountText, setAmountText] = useState("");
  const [day, setDay] = useState<IsoDate>(today);
  const [paidBy, setPaidBy] = useState(ME);
  const [kind, setKind] = useState<SplitKind>("equal");
  // Everyone takes part in an equal split unless unticked, including members added later.
  const [left, setLeft] = useState<ReadonlySet<string>>(new Set());
  const [parts, setParts] = useState<Record<string, string>>({});
  const [repeat, setRepeat] = useState<Repeat>("none");
  const [txId, setTxId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const amount = parseMoney(amountText);

  const linkable = useMemo(() => {
    if (paidBy !== ME || repeat !== "none") return [];
    const taken = new Set(group.expenses.flatMap((e) => (e.txId ? [e.txId] : [])));
    const since = new Date(Date.parse(today) - LINK_DAYS * 86_400_000).toISOString().slice(0, 10);
    return ledger
      .filter((tx) => tx.amount < 0 && tx.date >= since && !taken.has(tx.id))
      .sort((a, b) => (amount ? Math.abs(-a.amount - amount) - Math.abs(-b.amount - amount) : 0) || (a.date < b.date ? 1 : -1))
      .slice(0, 15);
  }, [ledger, paidBy, repeat, amount, group.expenses, today]);

  const rule = (): SplitRule => {
    if (kind === "equal") return { kind, members: members.filter((m) => !left.has(m.id)).map((m) => m.id) };
    const read = (text: string) => (kind === "exact" ? parseMoney(text) : kind === "percent" ? parsePercent(text) : Number(text.replace(",", ".")) || 0);
    return { kind, parts: Object.fromEntries(members.map((m) => [m.id, read(parts[m.id] ?? "") ?? 0])) };
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!description.trim() || !amount) {
      setError(t("groups.err.amount"));
      return;
    }
    const split = rule();
    const problem = splitProblem(amount, split);
    if (problem) {
      setError(t(`groups.err.${problem}`, { amount: money(amount) }));
      return;
    }
    const name = description.trim().slice(0, 120);
    if (repeat === "none") {
      store.saveGroup({ ...group, expenses: [...group.expenses, { id: uuid(), date: day, description: name, amount, paidBy, split, txId: txId || null }] });
    } else {
      store.saveGroup({ ...group, recurring: [...group.recurring, { id: uuid(), description: name, amount, paidBy, split, start: day, repeat, until: null }] });
    }
    announce(t("groups.expenseAdded", { name, amount: money(amount) }));
    setDescription("");
    setAmountText("");
    setTxId("");
    setParts({});
    setRepeat("none");
    setError(null);
  };

  const preview = amount && !splitProblem(amount, rule()) ? splitAmount(amount, rule()) : null;

  return (
    <form className="expense-form" onSubmit={submit} aria-labelledby={`${id}-title`} noValidate>
      <h3 id={`${id}-title`}>{t("groups.addExpense")}</h3>
      <div className="form-grid">
        <div className="field">
          <label htmlFor={`${id}-desc`}>{t("groups.what")}</label>
          <input type="text" id={`${id}-desc`} value={description} maxLength={120} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor={`${id}-amount`}>{t("groups.amount")}</label>
          <input type="text" id={`${id}-amount`} inputMode="decimal" value={amountText} onChange={(e) => setAmountText(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor={`${id}-date`}>{t("groups.date")}</label>
          <input id={`${id}-date`} type="date" value={day} onChange={(e) => setDay(e.target.value || today)} />
        </div>
        <div className="field">
          <label htmlFor={`${id}-paid`}>{t("groups.paidBy")}</label>
          <select id={`${id}-paid`} value={paidBy} onChange={(e) => setPaidBy(e.target.value)}>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.id === ME ? you : m.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`${id}-repeat`}>{t("groups.repeat")}</label>
          <select id={`${id}-repeat`} value={repeat} onChange={(e) => setRepeat(e.target.value as Repeat)}>
            {REPEATS.map((r) => (
              <option key={r} value={r}>
                {r === "none" ? t("groups.once") : t(`events.repeat.${r}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <fieldset>
        <legend>{t("groups.split")}</legend>
        <div className="choices">
          {KINDS.map((k) => (
            <label key={k}>
              <input type="radio" name={`${id}-kind`} checked={kind === k} onChange={() => setKind(k)} />
              {t(`groups.split.${k}`)}
            </label>
          ))}
        </div>
        <ul className="split-members">
          {members.map((m) => {
            const label = m.id === ME ? you : m.name;
            return (
              <li key={m.id}>
                {kind === "equal" ? (
                  <label>
                    <input
                      type="checkbox"
                      checked={!left.has(m.id)}
                      onChange={(e) => {
                        const next = new Set(left);
                        if (e.target.checked) next.delete(m.id);
                        else next.add(m.id);
                        setLeft(next);
                      }}
                    />
                    {label}
                  </label>
                ) : (
                  <label>
                    <span>{label}</span>
                    <input type="text"
                      inputMode="decimal"
                      size={8}
                      value={parts[m.id] ?? ""}
                      aria-label={t(`groups.part.${kind}`, { name: label })}
                      onChange={(e) => setParts({ ...parts, [m.id]: e.target.value })}
                    />
                    <span aria-hidden="true">{kind === "percent" ? "%" : kind === "exact" ? "$" : ""}</span>
                  </label>
                )}
                {preview && <span className="hint">{money(preview.get(m.id) ?? 0)}</span>}
              </li>
            );
          })}
        </ul>
      </fieldset>

      {linkable.length > 0 && (
        <div className="field">
          <label htmlFor={`${id}-tx`}>{t("groups.link")}</label>
          <select
            id={`${id}-tx`}
            value={txId}
            aria-describedby={`${id}-tx-help`}
            onChange={(e) => {
              setTxId(e.target.value);
              const tx = ledger.find((x) => x.id === e.target.value);
              if (tx) {
                if (!amountText) setAmountText((-tx.amount / 100).toFixed(2));
                setDay(tx.date);
                if (!description) setDescription(tx.description);
              }
            }}
          >
            <option value="">{t("groups.linkNone")}</option>
            {linkable.map((tx) => (
              <option key={tx.id} value={tx.id}>
                {date(tx.date, "short")} · {tx.description} · {money(-tx.amount)}
              </option>
            ))}
          </select>
          <p className="hint" id={`${id}-tx-help`}>
            {t("groups.linkHelp")}
          </p>
        </div>
      )}
      {repeat !== "none" && paidBy === ME && <p className="hint">{t("groups.recurringHelp")}</p>}

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="primary">
        {t("groups.addExpenseButton")}
      </button>
    </form>
  );
}
